import {
  EDatasourceType,
  EMQTTProtocolType,
  ESdChangeReason,
  IActionDefinitionDto,
  IDatasourceDto
} from "@kuika/kuika-cl-model";
import _, { delay } from "lodash";
import ObjectHash from "object-hash";
import { FormInstance } from "rc-field-form";
import * as React from "react";
import { connect } from "react-redux";
import { DatasourceService } from "../../../services/datasource-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { getProcessUsedActionIds } from "../../../shared/utility/process-action-usage";
import { SaveStatus } from "../../../shared/utility/save-status";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction, setShowModal } from "../../../store/unsaved-changes-modal/actions";
import { runParkedNavigation } from "../../../store/unsaved-changes-modal/run-parked-navigation";
import CloseConfirmationModal from "../../common/close-confirmation-modal";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaFormPage, {
  KuikaFormAsideBlock,
  KuikaFormField,
  KuikaFormPageVerifyResult,
  KuikaFormSection
} from "../../ui-kit/kuika-form-page/kuika-form-page";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSelectbox from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../ui-kit/kuika-switch/kuika-switch";
import { ConnectionRenameTitle } from "./connection-rename-title";
import { requestDatasourceReveal } from "./datasource-reveal";
import { ConnectionSwitcherChip } from "./editor-switcher/connection-switcher-chip";
import "./create-connection-form.scss";
import "./editor-bar-surface.scss";

/** How long the broker is given to answer before the probe is called a failure. */
const CONNECT_PROBE_MS: number = 2000;

/**
 * The rail's "After you create". The backend adds both actions on save
 * (`MqttDatasourceHelper.GenerateAutoGeneratedActions`); there is nothing to author after it.
 */
const MQTT_AFTER_CREATE: string = "CREATE saves the connection and adds its Publish and Subscribe actions.";

interface IMqttFormValues {
  validateCertificate?: boolean;
  useTls?: boolean;
  protocol?: EMQTTProtocolType;
  port?: number;
  basePath?: string;
  username?: string;
  password?: string;
  host?: string;
}

export interface ICreateConnectionMqttState {
  /** Where the save stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  isNameOnEdit: boolean;
  datasourceName: string;
  isTestLoading: boolean;
  /** Last Test outcome, shown as a chip in the rail. `null` until first run. */
  testResult: KuikaFormPageVerifyResult | null;
  /** The URL the fields add up to — what Test will actually dial. */
  connectUrl: string;
  actionCount: number;
  processActionCount: number;
  isPasswordVisible: boolean;
}

export interface ICreateConnectionMqttProps {
  editingDatasource?: IDatasourceDto;
  showModal: boolean;
  nextCallback?: () => void;
  setNextCallbackFunction: (cb?: () => void) => void;
  setShowModal: (showModal: boolean) => void;
}

class CreateConnectionMqtt extends PureSdComponent<
  ICreateConnectionMqttProps & IWithSDProps,
  ICreateConnectionMqttState
> {
  formRef = React.createRef<FormInstance>();

  objectHash: string = "";

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: ICreateConnectionMqttProps & IWithSDProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      isNameOnEdit: false,
      datasourceName: "",
      isTestLoading: false,
      testResult: null,
      connectUrl: "",
      actionCount: 0,
      processActionCount: 0,
      isPasswordVisible: false
    };
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  componentDidMount() {
    this.handleLoadDatasource();
    this.loadUsage();
  }

  componentDidUpdate(prevProps: ICreateConnectionMqttProps & IWithSDProps) {
    if (
      this.props.editingDatasource?.id !== prevProps.editingDatasource?.id ||
      this.props.sd.activeCreatingDatasourceName !== prevProps.sd.activeCreatingDatasourceName
    ) {
      this.handleLoadDatasource();
    }
    // A save the pending navigation asked for ("Update & close") is still running or showing
    // its ✓: its own close runs the navigation. Asking again now would re-open the confirmation.
    if (this.props.nextCallback && this.saveStatus.canStart) {
      this.handleCloseDatasource();
    }
  }

  // The two sources the canvas already reads for its "used in a process" banner, moved
  // beside the fields that would break it. Only meaningful once the datasource exists.
  private loadUsage = async (): Promise<void> => {
    const datasourceId: string | undefined = this.props.editingDatasource?.id;
    if (!datasourceId) return;

    const own: IActionDefinitionDto[] = (this.props.sd?.actionDefinitions ?? []).filter(
      (action) => String(action.kDatasource_Id) === String(datasourceId)
    );
    const usedInProcess: Set<string> = await getProcessUsedActionIds(
      this.props.sd?.workFlowList,
      this.props.sd?.project?.id
    );
    this.setState({
      actionCount: own.length,
      processActionCount: own.filter((action) => usedInProcess.has((action.id ?? "").toLowerCase())).length
    });
  };

  /**
   * The scheme is a function of the protocol and the TLS switch, so it is derived in one
   * place and used by both the rail and the probe — the two must never disagree about
   * what is being dialled.
   */
  private buildConnectUrl(values: IMqttFormValues): string {
    const scheme: string = values.protocol === EMQTTProtocolType.MQTT ? "mqtt" : "ws";
    const secure: string = values.useTls ? `${scheme}s` : scheme;
    return `${secure}://${values.host ?? ""}:${values.port ?? ""}/${values.basePath ?? ""}`;
  }

  private refreshConnectUrl = (): void => {
    const values: IMqttFormValues | undefined = this.formRef.current?.getFieldsValue();
    if (!values) return;
    this.setState({ connectUrl: this.buildConnectUrl(values) });
  };

  handleLoadDatasource = () => {
    const {
      editingDatasource,
      sd: { activeCreatingDatasourceName }
    } = this.props;

    let state = { ..._.cloneDeep(this.state) };
    const fieldsValue = {
      name: activeCreatingDatasourceName,
      validateCertificate: false,
      useTls: false,
      protocol: EMQTTProtocolType.WebSocket,
      port: undefined as number | undefined,
      basePath: undefined as string | undefined,
      username: undefined as string | undefined,
      password: undefined as string | undefined,
      host: undefined as string | undefined
    };

    state.datasourceName = activeCreatingDatasourceName ?? "";
    state.isNameOnEdit = false;

    if (editingDatasource) {
      fieldsValue.name = editingDatasource.name;
      state.datasourceName = editingDatasource.name;
      const info = editingDatasource.mqttConnectionInfo;
      if (info) {
        fieldsValue.validateCertificate = info.validateCertificate;
        fieldsValue.useTls = info.useTls;
        fieldsValue.protocol = info.protocol;
        fieldsValue.port = info.port;
        fieldsValue.basePath = info.basePath;
        fieldsValue.username = info.username;
        fieldsValue.password = info.password;
        fieldsValue.host = info.host;
      }
    }

    this.setState(state, () => {
      this.formRef.current?.setFieldsValue(fieldsValue);
      this.objectHash = this.getObjectHash(state);
      this.refreshConnectUrl();
    });
  };

  getObjectHash(state?: ICreateConnectionMqttState): string {
    const { datasourceName } = state || this.state;
    const values: IMqttFormValues = this.formRef.current?.getFieldsValue() ?? {};

    return ObjectHash({
      datasourceName,
      validateCertificate: values.validateCertificate,
      useTls: values.useTls,
      protocol: values.protocol,
      port: values.port,
      host: values.host,
      basePath: values.basePath,
      username: values.username,
      password: values.password
    });
  }

  closeDataSource() {
    if (!runParkedNavigation(this.props.nextCallback, this.props.setNextCallbackFunction)) {
      this.props.sd.cancelCreatingOrEditingDatasource();
    }
  }

  handleCloseDatasource() {
    if (this.objectHash === this.getObjectHash()) {
      this.closeDataSource();
      return;
    }

    this.props.setShowModal(true);
  }

  saveDatasource = async () => {
    if (this.state.isNameOnEdit) {
      KMainFunctions.displayWarningNotification(
        "You should apply your Datasource Name before save the MQTT Datasource"
      );
      return;
    }

    // The commit button is live again as soon as the ✓ replaces the spinner, so a second press
    // would re-fire the save behind the closing sheet.
    if (!this.saveStatus.canStart) return;
    this.saveStatus.start();
    const form: IMqttFormValues = this.formRef.current!.getFieldsValue();
    const ds = {} as IDatasourceDto;

    if (this.props.editingDatasource?.id) {
      ds.id = this.props.editingDatasource.id;
    }

    const isEqualToScreenName = this.props.sd?.screenList?.find(
      (screen) => screen.name.toLowerCase() === this.state.datasourceName?.toLowerCase()
    );
    const isEqualToWfName = this.props.sd?.workFlowList?.find(
      (wf) => wf.name.toLowerCase() === this.state.datasourceName?.toLowerCase()
    );
    const isEqualToDatasourceName = this.props.sd?.datasources?.some(
      (datasource) =>
        datasource.name.toLowerCase() === this.state.datasourceName?.toLowerCase() &&
        datasource.id !== this.props.editingDatasource?.id
    );
    if (isEqualToDatasourceName) {
      this.saveStatus.reset();
      KMainFunctions.displayWarningNotification("Datasource name already exist.");
      return;
    }
    if (isEqualToScreenName || isEqualToWfName) {
      this.saveStatus.reset();
      KMainFunctions.displayWarningNotification("Datasource name can not be equal to screen name or Process name.");
      return;
    }

    // Save is only reachable through the form's own submit, which validates first, so a
    // missing port means the invariant broke rather than the user forgot.
    if (form.port === undefined) {
      this.saveStatus.reset();
      KMainFunctions.displayWarningNotification("Please enter a port");
      return;
    }

    try {
      ds.datasourceType = EDatasourceType.Mqtt;
      ds.name = this.state.datasourceName;
      ds.mqttConnectionInfo = {
        // The switches and the protocol carry the form's own defaults; the DTO wants a
        // value for each rather than an absence.
        validateCertificate: form.validateCertificate ?? false,
        useTls: form.useTls ?? false,
        protocol: form.protocol ?? EMQTTProtocolType.WebSocket,
        port: form.port,
        basePath: form.basePath,
        username: form.username,
        password: form.password,
        host: form.host
      };

      const res = await DatasourceService.SaveDatasource(ds);
      if (res.status !== 200) {
        // A resolved response that is not 200: the `finally` reset used to rescue this branch.
        this.saveStatus.fail();
        return;
      }
      this.props.sd.refreshActions();
      await this.props.sd.refreshDatabases();
      // The ✓ goes up first and the sheet leaves after it — see `SaveStatus`.
      // A close confirmation this save answered ("Update & close") steps aside at once, so
      // the ✓ on the button is what is seen before the editor leaves.
      this.props.setShowModal(false);
      this.saveStatus.succeed((): void => {
        // The list behind the sheet lights the row up; the button has already said "Saved".
        requestDatasourceReveal({ name: ds.name });
        this.closeDataSource();
      });
    } catch (err) {
      this.saveStatus.fail();
      const backendMessage: string | undefined = KMainFunctions.getApiErrorMessage(err);
      KMainFunctions.displayWarningNotification(backendMessage ?? "Error while saving connection");
    }
  };

  /**
   * There is no backend probe for a broker, so this dials it from the browser: connect,
   * give it `CONNECT_PROBE_MS`, and report whichever came first. The outcome lands on the
   * rail's chip rather than a toast, beside the fields it refers to.
   */
  testConnection = () => {
    this.setState({ isTestLoading: true, testResult: null });
    this.formRef.current
      ?.validateFields()
      .then(async (formValues: IMqttFormValues) => {
        const connectUrl: string = this.buildConnectUrl(formValues);
        const scheme: string = connectUrl.split("://")[0];

        const mqttModule = await import(/* webpackChunkName: "mqtt" */ "mqtt");
        const mqtt = mqttModule.default;

        const client = mqtt.connect(connectUrl, {
          username: formValues.username,
          password: formValues.password,
          protocol: scheme === "wss" ? "wss" : scheme === "mqtts" ? "mqtts" : scheme === "ws" ? "ws" : "mqtt",
          rejectUnauthorized: formValues.validateCertificate
        });

        // Reported once: the timer and the error handler race, and the first to arrive
        // owns the chip.
        let settled: boolean = false;
        const settle = (ok: boolean, message: string): void => {
          if (settled) return;
          settled = true;
          this.setState({ testResult: { ok, message }, isTestLoading: false });
          client.end();
        };

        delay(() => {
          if (client.connected) {
            settle(true, `Reached ${connectUrl}`);
            return;
          }
          settle(false, `No answer from ${connectUrl}`);
        }, CONNECT_PROBE_MS);

        client.on("error", (error: Error) => {
          settle(false, error.message || "Connection failed");
        });
      })
      // A rejection here is the form's own validation result; the fields already say what
      // is missing, so the chip stays empty rather than repeating it.
      .catch((e) => {
        if (e?.errorFields) {
          this.setState({ isTestLoading: false });
          return;
        }
        this.setState({ testResult: { ok: false, message: e?.message ?? "Connection failed" }, isTestLoading: false });
      });
  };

  handleSubmitNameChange = () => {
    const errors = this.formRef.current?.getFieldError("name");
    if (errors && errors.length > 0) {
      return;
    }
    this.setState({ isNameOnEdit: false, datasourceName: this.formRef.current?.getFieldValue("name") });
  };

  handleAbortNameChange = () => {
    if (!this.state.datasourceName) {
      KMainFunctions.displayWarningNotification("Please enter a valid Datasource name");
      return;
    }

    this.formRef.current?.setFieldsValue({
      ...this.formRef.current.getFieldsValue(),
      name: this.state.datasourceName
    });
    this.setState({ isNameOnEdit: false });
  };

  private renderName = (title: string): React.ReactNode => (
    <ConnectionRenameTitle
      title={title}
      identity={
        <ConnectionSwitcherChip
          sd={this.props.sd}
          setNextCallbackFunction={this.props.setNextCallbackFunction}
          current={this.props.editingDatasource}
          name={this.state.datasourceName}
          datasourceType={EDatasourceType.Mqtt}
          triggerTestId="mqttConnectionSwitcherTrigger"
          onManageAll={() => this.handleCloseDatasource()}
        />
      }
      isEditing={this.state.isNameOnEdit}
      canDiscard={Boolean(this.state.datasourceName)}
      onBeginEdit={() => this.setState({ isNameOnEdit: true })}
      onSubmit={this.handleSubmitNameChange}
      onDiscard={this.handleAbortNameChange}
      placeholder="Datasource Name"
      rules={[
        { required: true, message: "Please enter a Datasource name" },
        { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Datasource name is invalid" }
      ]}
      renameTestId="mqttConnectionRenameButton"
      inputTestId="mqttConnectionNameInput"
    />
  );

  // Both blocks are conditional rather than shown empty: a datasource that does not exist
  // yet has no dependents, and the URL only reads as derived once a host is in.
  private renderAside(): React.ReactNode {
    const showUrl: boolean = !!this.formRef.current?.getFieldValue("host");
    const showUsage: boolean = !!this.props.editingDatasource?.id && this.state.actionCount > 0;
    if (!showUrl && !showUsage) return undefined;

    return (
      <>
        {showUrl && (
          <KuikaFormAsideBlock title="Resolves to">
            <code className="kuika-connection-form__resolved">{this.state.connectUrl}</code>
          </KuikaFormAsideBlock>
        )}
        {showUsage && (
          <KuikaFormAsideBlock title="Used by">
            <span className="kuika-connection-form__usage">
              {this.state.actionCount} {this.state.actionCount === 1 ? "action" : "actions"} on this datasource
            </span>
            {this.state.processActionCount > 0 && (
              <div className="kuika-connection-form__impact">
                <KuikaSvg name="alert-triangle" width={13} height={13} />
                <span>
                  <b>
                    {this.state.processActionCount} of them {this.state.processActionCount === 1 ? "runs" : "run"} in a
                    process.
                  </b>{" "}
                  Changing this connection affects older versions of that process.
                </span>
              </div>
            )}
          </KuikaFormAsideBlock>
        )}
      </>
    );
  }

  render() {
    return (
      <>
        <KuikaFormPage
          className="kuika-connection-form"
          title={this.state.datasourceName}
          formName="dsModal"
          formRef={this.formRef}
          onFinish={this.saveDatasource}
          onValuesChange={this.refreshConnectUrl}
          aside={this.renderAside()}
          afterCreate={this.props.editingDatasource ? undefined : MQTT_AFTER_CREATE}
          status={this.state.saveStatus}
          renderTitle={this.renderName}
          submitLabel={this.props.editingDatasource ? "UPDATE" : "CREATE"}
          submitTestId="mqttConnectionSubmitButton"
          cancelTestId="mqttConnectionCancelButton"
          onCancel={() => this.handleCloseDatasource()}
          verify={{
            label: "Test connection",
            onVerify: this.testConnection,
            isVerifying: this.state.isTestLoading,
            result: this.state.testResult,
            testId: "mqttTestConnectionButton",
            resultTestId: "mqttTestConnectionResult"
          }}
        >
          <KuikaFormSection legend="Broker" hint="Where Kuika connects" columns={2}>
            <KuikaFormField
              name="protocol"
              label="Protocol"
              rules={[{ required: true, message: "This field is required" }]}
            >
              {/* One option, as before: the MQTT transport is not wired up on the client. */}
              <KuikaSelectbox size="md" options={[{ label: "Websocket", value: EMQTTProtocolType.WebSocket }]} />
            </KuikaFormField>
            <KuikaFormField name="host" label="Host">
              <KuikaInput size="md" data-testid="mqttHostInput" />
            </KuikaFormField>
            <KuikaFormField name="port" label="Port" rules={[{ required: true, message: "This field is required" }]}>
              <KuikaInput size="md" type="number" data-testid="mqttPortInput" />
            </KuikaFormField>
            <KuikaFormField name="basePath" label="Base path">
              <KuikaInput size="md" data-testid="mqttBasePathInput" />
            </KuikaFormField>
          </KuikaFormSection>

          <KuikaFormSection legend="Credentials" hint="Sent to the broker on connect" columns={2}>
            {/* The broker's credentials, not the designer's own login — see the SQL connection
                form: without these the password manager fills the Kuika account in here. */}
            <KuikaFormField name="username" label="Username">
              <KuikaInput size="md" autoComplete="off" data-testid="mqttUsernameInput" />
            </KuikaFormField>
            <KuikaFormField name="password" label="Password">
              <KuikaInput
                size="md"
                autoComplete="new-password"
                type={this.state.isPasswordVisible ? "text" : "password"}
                data-testid="mqttPasswordInput"
                rightUnit={
                  <KuikaSvg
                    name={this.state.isPasswordVisible ? "eye" : "eye-closed"}
                    pointer
                    onClick={() => this.setState({ isPasswordVisible: !this.state.isPasswordVisible })}
                  />
                }
              />
            </KuikaFormField>
          </KuikaFormSection>

          <KuikaFormSection legend="Transport security" hint="Applies to the URL above" columns={2}>
            <KuikaFormField name="useTls" label="Use TLS" valuePropName="checked">
              <KuikaSwitch />
            </KuikaFormField>
            <KuikaFormField
              name="validateCertificate"
              label="Validate certificate"
              valuePropName="checked"
              extra="Off accepts a self-signed certificate."
            >
              <KuikaSwitch />
            </KuikaFormField>
          </KuikaFormSection>
        </KuikaFormPage>

        <CloseConfirmationModal
          itemName={this.state.datasourceName}
          closeType={"MQTT Connection"}
          triggerModal={this.props.showModal}
          isLoading={this.state.saveStatus === "loading"}
          updateAndCloseHandler={() => this.formRef.current?.submit()}
          closeWithoutSaveHandler={() => {
            this.props.setShowModal(false);
            this.closeDataSource();
          }}
          cancel={() => {
            this.props.setNextCallbackFunction();
            this.props.setShowModal(false);
          }}
        />
      </>
    );
  }

  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapStateToProps = (state: AppState) => ({
  nextCallback: state.unsavedChangesModal.nextCallback,
  showModal: state.unsavedChangesModal.showModal
});

const mapDispatchToProps = (dispatch) => ({
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb)),
  setShowModal: (showModal: boolean) => dispatch(setShowModal(showModal, null))
});

const createConnectionMqtt = connect(
  mapStateToProps,
  mapDispatchToProps
)(withSD<ICreateConnectionMqttProps>(CreateConnectionMqtt));
export { createConnectionMqtt as CreateConnectionMqtt };
